<template>
    <div v-if="trip" class="pre-selection-custom">
        <FormV2
            id="form_transfer_pre_search"
            :key="formId"
            :onSubmit="handleSubmit"
            :initialValues="initialValues"
            :validationSchema="validationSchema">
            <Requirement :trip="trip" :onScroll="onClose" />

            <Notification v-if="description" :tier="1" :title="description.title">
                {{ description.text }}
            </Notification>

            <div class="pre-selection-custom-fields">
                <component
                    v-for="field in fields"
                    :key="field.name"
                    :is="field.component"
                    :name="field.name"
                    v-model="field.value"
                    :input="{ label: t({ key: field.labelKey }) }" />
            </div>

            <Notification v-if="messageWarning" variant="warning" icon="InfoCircle">
                {{ messageWarning }}
            </Notification>

            <Notification
                v-if="messageInvalid"
                variant="negative"
                icon="InfoCircle"
                :title="t({ key: 'shared.check_pickup_time' })">
                {{ messageInvalid }}
            </Notification>

            <div class="pre-selection-custom-footer">
                <ButtonPrimary type="submit" :disabled="disabled">
                    {{ t({ key: 'shared.search' }) }}
                </ButtonPrimary>
            </div>
        </FormV2>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import { defineComponent, ref, watch, computed, PropType } from 'vue';
    import {
        TransferPickupValidateResponse,
        TransferValidateType,
        TransferTrip,
        TransferPickupConditionDescription,
        TransferSearchCondition,
    } from '@/helpers/transfer/transfer.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import {
        Validation,
        ValidationSchema,
    } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { isTime, isDate } from '@ui/components/FormV2/Validations/functions';
    import { generateUUID } from '@shared/utils/uuid';
    import { getTransferSearchConditions } from '@/helpers/transfer/transfer.conditions';
    import {
        addTimeToDate,
        convertToDate,
        getShortDate,
        getTime,
    } from '@shared/helpers/datetime/datetime';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import TimeField from '@ui/components/FormV2/Fields/Time/TimeField.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import DateInputField from '@ui/components/FormV2/Fields/DateInput/DateInputField.vue';
    import Requirement from '../../Shared/Requirement.vue';

    interface FormField {
        name: string;
        labelKey: string;
        value: any;
        validation: Validation[];
        component: any;
    }

    export default defineComponent({
        name: 'TransferPreSelectionCustom',
        methods: { t },
        components: {
            FormV2,
            TimeField,
            DateInputField,
            Notification,
            ButtonPrimary,
            Requirement,
        },
        props: {
            trip: { type: Object as PropType<TransferTrip>, required: true },
            onSearch: { type: Function, required: true },
            onClose: { type: Function, required: true },
        },
        setup(props) {
            const store = useStore();

            const formId = ref<string | undefined>(undefined);

            const fieldDate = ref<string>(
                props.trip.request.startTime
                    ? getShortDate(convertToDate(props.trip.request.startTime))
                    : ''
            );
            const fieldPickUpTime = ref<string>(
                props.trip.request.startTime
                    ? getTime(convertToDate(props.trip.request.startTime))
                    : ''
            );

            const validateResponse = ref<TransferPickupValidateResponse | undefined>(
                undefined
            );

            const initialValues = ref<FormValues | undefined>(undefined);
            const validationSchema = ref<ValidationSchema | undefined>(undefined);

            const fields = computed<FormField[]>(() => getFields());

            const conditions = computed<TransferSearchCondition | undefined>(() =>
                getTransferSearchConditions(props.trip)
            );

            const messageWarning = computed<string | undefined>(() => {
                if (validateResponse?.value?.type === TransferValidateType.Warning) {
                    return validateResponse?.value?.message;
                }

                return undefined;
            });

            const messageInvalid = computed<string | undefined>(() => {
                if (validateResponse?.value?.type === TransferValidateType.Invalid) {
                    return validateResponse?.value?.message;
                }

                return undefined;
            });

            const description = computed<TransferPickupConditionDescription | undefined>(
                () => conditions.value?.description
            );

            const disabled = computed<boolean>(
                () => validateResponse.value?.type === TransferValidateType.Invalid
            );

            function setInitialState(fields: FormField[]) {
                initialValues.value = setInitialValues(fields);
                validationSchema.value = setValidationSchema(fields);

                // We do this to reinitialize the form due to intialValues / validationSchema changes
                formId.value = generateUUID();
            }

            function getFields(): FormField[] {
                const fields: FormField[] = [
                    {
                        name: 'Date',
                        labelKey: 'shared.date',
                        component: DateInputField,
                        value: fieldDate.value,
                        validation: [isDate],
                    },
                    {
                        name: 'PickUpTime',
                        labelKey: 'shared.pickup_time',
                        component: TimeField,
                        value: fieldPickUpTime.value,
                        validation: [isTime],
                    },
                ];

                return fields;
            }

            function setValidationSchema(fields: FormField[]) {
                const schema: ValidationSchema = {};

                fields?.forEach((f) => {
                    if (f) {
                        schema[f.name] = f.validation;
                    }
                });

                return schema;
            }

            function setInitialValues(fields: FormField[]) {
                const values: FormValues = {};

                fields?.forEach((f) => {
                    if (f) {
                        values[f.name] = f.value;
                    }
                });

                return values;
            }

            function handleSubmit(values: any) {
                if (values) {
                    const newStartTime = addTimeToDate(values.Date, values.PickUpTime);

                    store
                        .dispatch('checkout/setTransferTripStartTime', {
                            id: props.trip.id,
                            startTime: newStartTime,
                        })
                        .then(() => props.onSearch());
                }
            }

            function validatePickUpTime(): TransferPickupValidateResponse | undefined {
                if (conditions.value?.validate) {
                    const date = addTimeToDate(fieldDate.value, fieldPickUpTime.value);
                    return conditions.value.validate(convertToDate(date));
                }

                return undefined;
            }

            watch(
                () => fields.value,
                (newFields, prevFields) => {
                    if (newFields.length !== prevFields?.length) {
                        setInitialState(newFields);
                    }
                },
                { immediate: true }
            );

            watch(
                () => [fieldDate.value, fieldPickUpTime.value],
                (newFields) => {
                    const [date, time] = newFields;
                    if (date && time) {
                        validateResponse.value = validatePickUpTime();
                    } else {
                        validateResponse.value = undefined;
                    }
                },
                { immediate: true }
            );

            return {
                description,
                formId,
                fields,
                validationSchema,
                initialValues,
                messageWarning,
                messageInvalid,
                disabled,
                handleSubmit,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './PreSelectionCustom.scss';
</style>
